import React, { useMemo, useState } from 'react';
import {
  AccessibilityReport,
  AccessibilityFinding,
  AccessibilityTier,
  AccessibilitySeverity,
} from '../types/accessibilityReport';
import { recommendationFor, REMEDIATION_REFERENCE_PATH } from '../utils/remediationRecommendations';
import { buildShareableText, headingCount } from '../utils/accessibilityReportText';

/**
 * The report card the author sees at upload (docs/design/ACCESSIBILITY_AUDIT_ON_UPLOAD.md).
 *
 * Three tiers — DOCUMENT, CONTENT, INTERACTIVE — each showing what the document HAS (the observed
 * facts) and what it LACKS (the findings). Every finding states its problem in plain English and
 * carries its top recommended fix, mapped from its remediation key. The key is shown; the full fix
 * sheet (REMEDIATION_REFERENCE.md) stays the source of truth, so the card never duplicates it.
 *
 * HONESTY IS THE POSTURE, NOT A HEDGE. Advisory findings — the heuristic "looks like a heading"
 * guesses — are rendered visibly marked as advisory and are never asserted as fact. The card
 * claims no conformance and names no standard as met; it states state.
 */

interface AccessibilityReportCardProps {
  report: AccessibilityReport;
  filename?: string;
}

const TIER_LABELS: Record<AccessibilityTier, string> = {
  DOCUMENT: 'Document',
  CONTENT: 'Content',
  INTERACTIVE: 'Interactive',
};

const TIER_BLURB: Record<AccessibilityTier, string> = {
  DOCUMENT: 'How the file declares itself: tagging, language, and title.',
  CONTENT: 'Whether the words and pictures on the page are in the reading-order tree.',
  INTERACTIVE: 'Whether form fields and links are named and navigable.',
};

/** One fact about the document: a label, a value, and whether that value is good/neutral/bad. */
interface Fact {
  label: string;
  value: string;
  tone: 'good' | 'bad' | 'neutral';
}

const SEVERITY_STYLE: Record<AccessibilitySeverity, { badge: string; label: string }> = {
  CRITICAL: { badge: 'bg-error-100 text-error-800 border-error-200', label: 'Critical' },
  SERIOUS: { badge: 'bg-orange-100 text-orange-800 border-orange-200', label: 'Serious' },
  MODERATE: { badge: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Moderate' },
  ADVISORY: { badge: 'bg-sky-100 text-sky-800 border-sky-200', label: 'Advisory' },
};

function documentFacts(report: AccessibilityReport): Fact[] {
  const d = report.document;
  if (!d) {
    return [];
  }
  return [
    { label: 'Declared tagged', value: d.marked ? 'Yes' : 'No', tone: d.marked ? 'good' : 'bad' },
    {
      label: 'Structure tree',
      value: d.hasStructureTree ? 'Present' : 'Absent',
      tone: d.hasStructureTree ? 'good' : 'bad',
    },
    {
      label: 'Language',
      value: d.language ? d.language : 'Not set',
      tone: d.language ? 'good' : 'bad',
    },
    {
      label: 'Title announced',
      value: d.hasDcTitle && d.displayDocTitle ? 'Yes' : 'No',
      tone: d.hasDcTitle && d.displayDocTitle ? 'good' : 'bad',
    },
    {
      label: 'PDF/UA claim',
      value: d.hasPdfUaIdentifier ? 'Present' : 'None',
      // A missing claim is neutral, not bad: declining to claim what a file has not earned is right.
      tone: 'neutral',
    },
  ];
}

function contentFacts(report: AccessibilityReport): Fact[] {
  const c = report.content;
  if (!c) {
    return [];
  }
  const headings = headingCount(c.histogram);
  const paragraphs = c.histogram?.['P'] ?? 0;
  const figures = c.histogram?.['Figure'] ?? 0;
  const total = c.totalElements ?? 0;
  const images = c.imageCount ?? 0;
  return [
    { label: 'Tagged elements', value: String(total), tone: total > 0 ? 'good' : 'bad' },
    { label: 'Headings (H1..Hn)', value: String(headings), tone: headings > 0 ? 'good' : 'neutral' },
    { label: 'Paragraphs (P)', value: String(paragraphs), tone: paragraphs > 0 ? 'good' : 'neutral' },
    // Two distinct populations, kept as two facts — NOT "X of Y". Figure elements can wrap vector
    // graphics (logos, rules) that are no raster image, and one image can be painted on many pages,
    // so figures routinely exceed raster images; "34 of 6" read as a ratio and was nonsense. The
    // real "more images painted than tagged" signal is a FINDING (FIGURE-NO-ALT heuristic), not a
    // tone on this count, so these two rows are plain and informational.
    { label: 'Figures tagged', value: String(figures), tone: figures > 0 ? 'good' : 'neutral' },
    { label: 'Images drawn on pages', value: String(images), tone: 'neutral' },
  ];
}

function interactiveFacts(report: AccessibilityReport): Fact[] {
  const i = report.interactive;
  if (!i) {
    return [];
  }
  const fieldCount = i.fieldCount ?? 0;
  const named = fieldCount - (i.fieldsMissingTu ?? 0);
  const linkCount = i.linkCount ?? 0;
  const described = linkCount - (i.linksMissingContents ?? 0);
  return [
    {
      label: 'Form fields named',
      value: `${named} of ${fieldCount}`,
      tone: fieldCount === 0 ? 'neutral' : named >= fieldCount ? 'good' : 'bad',
    },
    {
      label: 'Links described',
      value: `${described} of ${linkCount}`,
      tone: linkCount === 0 ? 'neutral' : described >= linkCount ? 'good' : 'bad',
    },
  ];
}

const FACTS_BY_TIER: Record<AccessibilityTier, (r: AccessibilityReport) => Fact[]> = {
  DOCUMENT: documentFacts,
  CONTENT: contentFacts,
  INTERACTIVE: interactiveFacts,
};

const toneDot: Record<Fact['tone'], string> = {
  good: 'bg-success-500',
  bad: 'bg-error-400',
  neutral: 'bg-neutral-300',
};

const FindingRow: React.FC<{ finding: AccessibilityFinding }> = ({ finding }) => {
  const style = SEVERITY_STYLE[finding.severity];
  const rec = recommendationFor(finding.key);
  return (
    <li className="border border-neutral-200 rounded-lg p-3 bg-white">
      <div className="flex items-start gap-2 flex-wrap">
        <span
          className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${style.badge}`}
        >
          {style.label}
        </span>
        {finding.advisory && (
          // The honesty flag made visible. An advisory finding is a heuristic guess, so it is
          // marked as one — "looks like…, not tagged" — never asserted as a fact.
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-sky-50 text-sky-700 border-sky-200"
            title="A heuristic hint based on layout, not an asserted fact. The judgment is yours."
          >
            Advisory — not asserted
          </span>
        )}
        <code className="text-xs text-neutral-500 ml-auto">{finding.key}</code>
      </div>
      <p className="text-sm text-neutral-800 mt-2">{finding.statement}</p>
      <p className="text-sm text-neutral-600 mt-1">
        <span className="font-semibold text-neutral-700">Recommended: </span>
        {rec.summary} <span className="text-xs text-neutral-400">[{rec.doer}]</span>
      </p>
    </li>
  );
};

const TierBlock: React.FC<{ tier: AccessibilityTier; report: AccessibilityReport }> = ({
  tier,
  report,
}) => {
  const facts = FACTS_BY_TIER[tier](report);
  const findings = report.findings.filter(f => f.tier === tier);
  return (
    <section className="border border-neutral-200 rounded-xl p-4" aria-label={`${TIER_LABELS[tier]} tier`}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h4 className="text-base font-semibold text-neutral-900">{TIER_LABELS[tier]}</h4>
        <span className="text-xs text-neutral-500">
          {findings.length === 0
            ? 'Nothing flagged'
            : `${findings.length} finding${findings.length === 1 ? '' : 's'}`}
        </span>
      </div>
      <p className="text-xs text-neutral-500 mt-0.5">{TIER_BLURB[tier]}</p>

      {/* What this document HAS */}
      {facts.length > 0 && (
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 mt-3">
          {facts.map(fact => (
            <div key={fact.label} className="flex items-center gap-2 text-sm">
              <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${toneDot[fact.tone]}`} />
              <dt className="text-neutral-500">{fact.label}:</dt>
              <dd className="text-neutral-800 font-medium">{fact.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {/* What it LACKS */}
      {findings.length > 0 && (
        <ul className="space-y-2 mt-3">
          {findings.map((finding, idx) => (
            <FindingRow key={`${finding.key}-${idx}`} finding={finding} />
          ))}
        </ul>
      )}
    </section>
  );
};

const AccessibilityReportCard: React.FC<AccessibilityReportCardProps> = ({ report, filename }) => {
  const [copied, setCopied] = useState(false);

  const shareableText = useMemo(() => buildShareableText(report, filename), [report, filename]);
  const totalFindings = report.findings.length;
  const nonAdvisory = report.findings.filter(f => !f.advisory).length;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareableText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard may be unavailable (insecure context, denied permission). The plain text is
      // still on screen in the shareable panel below, so the author can select and copy by hand;
      // a failed programmatic copy must not throw an error at them.
      setCopied(false);
    }
  };

  const [showShareable, setShowShareable] = useState(false);

  return (
    <div className="bg-white rounded-2xl shadow-soft-lg p-6 border border-neutral-200" data-testid="accessibility-report-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-display font-semibold text-neutral-900">
              Accessibility report
            </h3>
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200"
              title="These findings have not yet been verified against an external PDF/UA validator."
            >
              Experimental
            </span>
          </div>
          <p className="text-sm text-neutral-500 mt-0.5">
            What your uploaded file has and what it lacks. This is a diagnosis, not a certificate —
            it makes no conformance claim, and it never leaves your server.
            <span className="block mt-1 text-amber-700">
              Experimental: these findings are not yet verified against an external PDF/UA
              validator (veraPDF) — treat them as indicative, not authoritative, and please verify
              these results against your own independent findings.
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleCopy}
            className="text-sm px-3 py-1.5 rounded-lg border border-neutral-300 text-neutral-700 hover:bg-neutral-50"
          >
            {copied ? 'Copied' : 'Copy report'}
          </button>
          <button
            type="button"
            onClick={() => setShowShareable(v => !v)}
            className="text-sm px-3 py-1.5 rounded-lg border border-neutral-300 text-neutral-700 hover:bg-neutral-50"
          >
            {showShareable ? 'Hide plain text' : 'Plain text'}
          </button>
        </div>
      </div>

      {/* Honest one-line summary of state — a count, never a grade. */}
      <p className="text-sm text-neutral-600 mb-4">
        {totalFindings === 0 ? (
          <span className="text-success-700 font-medium">
            No accessibility gaps were detected in this scan.
          </span>
        ) : (
          <>
            <span className="font-medium text-neutral-800">{totalFindings}</span> item
            {totalFindings === 1 ? '' : 's'} to look at
            {nonAdvisory !== totalFindings && (
              <> ({totalFindings - nonAdvisory} advisory)</>
            )}
            .
          </>
        )}
      </p>

      <div className="space-y-3">
        {(['DOCUMENT', 'CONTENT', 'INTERACTIVE'] as AccessibilityTier[]).map(tier => (
          <TierBlock key={tier} tier={tier} report={report} />
        ))}
      </div>

      {showShareable && (
        <div className="mt-4">
          <p className="text-xs text-neutral-500 mb-1">
            Plain-text version — select to copy, or print this panel. The authoritative fixes live
            in <code>{REMEDIATION_REFERENCE_PATH}</code>.
          </p>
          <pre
            className="text-xs bg-neutral-50 border border-neutral-200 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap text-neutral-800"
            data-testid="accessibility-report-plaintext"
          >
            {shareableText}
          </pre>
        </div>
      )}
    </div>
  );
};

export default AccessibilityReportCard;
